import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  SpaceBetween,
  Container,
  Header,
  Button,
} from "@amzn/awsui-components-react";
import Page from "components/page";
import { FilePreview } from "components/file-preview/FilePreview";
import CreateEditGaiaobjectForm from "components/create-edit-gaiaobject-form/CreateEditGaiaobjectForm";
import TaskSuggestions from "components/task-suggestions/TaskSuggestions";
import ApiIndicator from "components/api-indicator";
import { useBreadcrumbs } from "features/breadcrumbs";
import { useGetScanjobQuery, useGetScanResultQuery } from "./endpoints";
import { useDownloadUrlQuery } from "components/documents-table/endpoints";
import { useCreateTasksMutation } from "components/task-group/endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { getFileNameWithoutExtension } from "utils/date-format";
import { IDraftOverviewProps } from "./definitions";
import Help from "./Help";
import styles from "./style.module.scss";
import PermissionBoundary from "components/permission-boundary";
import { useAppSelector } from "app/store";
import { useNotifications } from "features/notifications";
import Loader from "components/loader";
import DraftsWizard from "components/draft-wizard/DraftWizard";
import { createTasksPayloads } from "utils/taskTransformUtils";
import { GaiaConstants } from "utils/gaia-constants";
import {
  setTaskData,
  clearTaskData,
  useGetTaskData,
} from "features/draft-wizard";
import { useAppDispatch } from "app/store";
import { useSelectedTeam } from "features/team-settings/hooks";
import { NoticeDraftWizard } from "components/notice-draft-wizard";
import { GAIAOBJECT_CATEGORY } from "utils/gaia-constants";

const DraftOverview: React.FC<IDraftOverviewProps> = () => {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const taskData = useGetTaskData(shortId || "");
  const [createTasks] = useCreateTasksMutation();
  const [updateScanJobCall] = useUpdateScanJobMutation();
  const [registerDocumentCall] = useRegisterDocumentMutation();
  const [uploadUrlCall] = useUploadUrlMutation();
  const teamName = useAppSelector((state) => state.audits.teamName);
  const { isFileScanEnabled, isFileScanAssessmentEnabled } = useSelectedTeam();
  const [isCreating, setIsCreating] = useState(false);
  const [isFilePreviewVisible, setIsFilePreviewVisible] = useState(true);

  const {
    data: scanJobData,
    error: scanJobError,
    isLoading: scanJobLoading,
    isError: scanJobIsError,
  } = useGetScanjobQuery(
    { shortId: shortId as string },
    { refetchOnMountOrArgChange: true, skip: !shortId }
  );
  const { data: scanResultData } = useGetScanResultQuery(
    { shortId: shortId! },
    { refetchOnMountOrArgChange: true, skip: !shortId }
  );

  const {
    data: downloadUrlData,
    error: downloadUrlError,
    isLoading: downloadUrlLoading,
    isError: downloadUrlIsError,
  } = useDownloadUrlQuery(
    { documentId: shortId as string, preview: true },
    {
      skip: !shortId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );

  useEffect(() => {
    updateBreadcrumbs &&
      updateBreadcrumbs([
        { text: "Gaia", href: "/" },
        { text: "Drafts", href: "/drafts" },
        { text: `${shortId || "..."}`, href: "#" },
      ]);
  }, [shortId]);

  useEffect(() => {
    if (
      !scanJobIsError &&
      scanJobData?.scanJob &&
      scanJobData.scanJob.status !== "Ready for review"
    ) {
      addNotification({
        type: "info",
        header: "Review is already complete",
        content: `This draft cannot be edited because its status is "${scanJobData.scanJob.status}".`,
        dismissible: true,
        timeout: 5000,
      });
      navigate("/drafts");
    }
  }, [scanJobData?.scanJob?.status, scanJobIsError]);

  if (scanJobLoading) {
    return <Loader loadingText="Loading draft..." />;
  }

  if (!scanJobIsError && scanJobData?.scanJob) {
    const scanJob = scanJobData.scanJob;

    if (scanJob.category === "Audit" && isFileScanEnabled) {
      return <DraftsWizard />;
    }
    if (isFileScanAssessmentEnabled) {
      return (
        <NoticeDraftWizard
          category={scanJob.category.toLowerCase() as GAIAOBJECT_CATEGORY}
        />
      );
    }
  }
  const isLoading = downloadUrlLoading;
  const isError = scanJobIsError || downloadUrlIsError;
  const error = scanJobError || downloadUrlError;

  const category = scanJobData?.scanJob?.category?.toLowerCase();

  const handleCreateTasks = async () => {
    if (isCreating) return;
    if (!taskData || taskData.length === 0) {
      addNotification({
        type: "error",
        header: "No tasks to create",
        content: "Please add some tasks before creating.",
        dismissible: true,
        timeout: 10000,
      });
      return;
    }

    const gaiaObjectId = scanJobData?.scanJob?.gaiaObjectId;
    if (!gaiaObjectId) {
      addNotification({
        type: "error",
        header: "Missing Gaia object ID",
        content: "Cannot create tasks without a valid Gaia object ID.",
        dismissible: true,
        timeout: 10000,
      });
      return;
    }

    setIsCreating(true);

    const payloads = createTasksPayloads(
      taskData,
      gaiaObjectId,
      teamName,
      scanJobData?.scanJob?.createdBy
    );

    try {
      for (let i = 0; i < payloads.length; i++) {
        await createTasks(payloads[i]).unwrap();
      }
    } catch (e: any) {
      addNotification({
        type: "error",
        header: "Failed to create tasks",
        content: e?.data?.message || "An error occurred while creating tasks.",
        dismissible: true,
        timeout: 10000,
      });
      setIsCreating(false);
      return;
    }

    if (scanJobData?.scanJob?.fileName && downloadUrlData?.url) {
      try {
        const uploadRequest = {
          prefix: `audits/${gaiaObjectId}`,
          fileName: scanJobData.scanJob.fileName,
          teamName: teamName,
          securityLevel: "Normal",
        };
        const uploadResponse = await uploadUrlCall(uploadRequest).unwrap();
        const originalFileResponse = await fetch(downloadUrlData.url);
        const fileBlob = await originalFileResponse.blob();

        await fetch(uploadResponse.url, {
          method: "PUT",
          body: fileBlob,
          headers: {
            "x-amz-expected-bucket-owner": uploadResponse.bucketOwner,
          },
        });

        const s3Prefix = `audits/${gaiaObjectId}/${uploadResponse.uniqueKey}/${scanJobData.scanJob.fileName}`;
        const registerDocumentRequest = {
          parentId: gaiaObjectId,
          teamName: teamName,
          fileName: getFileNameWithoutExtension(scanJobData.scanJob.fileName),
          documentType: "Audit notices",
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          extentionType: scanJobData.scanJob.extensionType,
          auditId: gaiaObjectId,
          size: scanJobData.scanJob.size,
        };
        await registerDocumentCall(registerDocumentRequest).unwrap();
      } catch (documentError: any) {
        console.error("Failed to register document:", documentError);
      }
    }

    try {
      await updateScanJobCall({
        shortId: shortId as string,
        gaiaObjectId: gaiaObjectId,
        status: "Review complete",
      }).unwrap();

      addNotification({
        type: "success",
        header: "Tasks created successfully",
        dismissible: true,
        timeout: 10000,
      });
      dispatch(clearTaskData({ scanJobId: shortId || "" }));
      navigate("/drafts");
    } catch (e: any) {
      addNotification({
        type: "error",
        header: "Failed to update draft status",
        content:
          e?.data?.message ||
          "Tasks were created successfully, but failed to update the draft status.",
        dismissible: true,
        timeout: 10000,
      });
      setIsCreating(false);
    }
  };

  const renderFormComponent = () => {
    if (category === "task group") {
      const template = scanResultData?.scanTaskResult?.template;
      return (
        <TaskSuggestions
          shortId={shortId}
          auditData={scanJobData}
          teamName={teamName}
          templateId={template}
          onTaskDataChange={(newTaskData) =>
            dispatch(
              setTaskData({
                scanJobId: shortId || "",
                taskData: newTaskData,
              })
            )
          }
          initialTaskData={taskData}
        />
      );
    } else {
      return (
        <CreateEditGaiaobjectForm
          type="notice"
          mode="draft"
          shortId={shortId}
        />
      );
    }
  };

  const renderPageActions = () => {
    if (category === "task group") {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            formAction="none"
            onClick={() => navigate("/drafts")}
            variant="link"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTasks}
            disabled={isCreating}
          >
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </SpaceBetween>
      );
    }
    return null;
  };

  return (
    <PermissionBoundary allowMemberOnly={true}>
      <ApiIndicator
        isLoading={isLoading}
        isError={isError}
        isAlertError={true}
        error={error}
      />
      <Page
        type="page"
        title={t("pages.draft_overview.title")}
        hasBreadcrumbs={true}
        hasInfo={false}
        help={<Help />}
        className={styles.draftOverview}
      >
        <SpaceBetween direction="vertical" size="xl">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
              onClick={() => setIsFilePreviewVisible(!isFilePreviewVisible)}
            >
              {isFilePreviewVisible ? "Hide" : "Show"} File Preview
            </Button>
          </SpaceBetween>
          <Grid
            gridDefinition={
              isFilePreviewVisible
                ? [{ colspan: 6 }, { colspan: 6 }]
                : [{ colspan: 12 }]
            }
          >
            {isFilePreviewVisible && (
              <Container header={<Header variant="h2">File Preview</Header>}>
                {scanJobData?.scanJob ? (
                  <FilePreview
                    type={"pdf"}
                    fileName={scanJobData.scanJob.fileName}
                    presignedUrl={downloadUrlData?.url || ""}
                  />
                ) : (
                  <div>No file available for preview</div>
                )}
              </Container>
            )}
            {category === "task group" ? (
              <Container>{renderFormComponent()}</Container>
            ) : (
              renderFormComponent()
            )}
          </Grid>
          {renderPageActions() && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {renderPageActions()}
            </div>
          )}
        </SpaceBetween>
      </Page>
    </PermissionBoundary>
  );
};

export default DraftOverview;
